import React, { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  parseISO,
  startOfMonth,
} from "date-fns";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Package,
  RefreshCw,
  Truck,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { STATUS_COLORS } from "./utils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const groupLabel = (group) => (group === "B" ? "Bridal" : "Sider");

const deliveryTypeLabel = (delivery) => (
  delivery.fulfillment_type === "store_pickup" ? "Shop Collection" : "Home Delivery"
);

const localDateForKey = (value) => {
  const parsed = parseISO(value || "");
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const kolkataToday = () => {
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(
    parts
      .filter((part) => ["year", "month", "day"].includes(part.type))
      .map((part) => [part.type, part.value])
  );
  return new Date(Number(values.year), Number(values.month) - 1, Number(values.day));
};

export const DeliveryCalendar = ({ me, onOpenInvoice, refreshKey = 0 }) => {
  const isAdmin = me?.role === "admin";
  const fixedGroup = me?.role === "staff_group_b" ? "B" : "A";
  const [selectedGroup, setSelectedGroup] = useState(fixedGroup);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(kolkataToday()));
  const [selectedDate, setSelectedDate] = useState(() => format(kolkataToday(), "yyyy-MM-dd"));
  const [data, setData] = useState({ deliveries: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);
  const monthKey = format(visibleMonth, "yyyy-MM");

  useEffect(() => {
    setSelectedGroup(fixedGroup);
  }, [fixedGroup]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/admin/calendar-deliveries", {
      params: { month: monthKey, group: selectedGroup },
    })
      .then(({ data: response }) => {
        if (!cancelled) {
          setData({
            deliveries: Array.isArray(response?.deliveries) ? response.deliveries : [],
            summary: response?.summary || null,
          });
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(formatError(requestError) || "Unable to load the delivery calendar.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [monthKey, refreshKey, requestVersion, selectedGroup]);

  const deliveriesByDate = useMemo(() => {
    const grouped = new Map();
    data.deliveries.forEach((delivery) => {
      const key = delivery.internal_delivery_date;
      const parsed = localDateForKey(key);
      if (!key || !parsed || !key.startsWith(monthKey)) {
        return;
      }
      grouped.set(key, [...(grouped.get(key) || []), delivery]);
    });
    return grouped;
  }, [data.deliveries, monthKey]);

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(visibleMonth), end: endOfMonth(visibleMonth) }),
    [visibleMonth]
  );
  const emptyCells = Array.from({ length: getDay(days[0]) }, (_, index) => index);
  const selectedDeliveries = deliveriesByDate.get(selectedDate) || [];
  const selectedLabel = localDateForKey(selectedDate)
    ? format(localDateForKey(selectedDate), "EEEE, d MMMM yyyy")
    : "Selected date";
  const today = kolkataToday();

  const changeMonth = (offset) => {
    const next = startOfMonth(addMonths(visibleMonth, offset));
    setVisibleMonth(next);
    setSelectedDate(format(next, "yyyy-MM-dd"));
  };

  const goToToday = () => {
    const next = startOfMonth(today);
    setVisibleMonth(next);
    setSelectedDate(format(today, "yyyy-MM-dd"));
  };

  return (
    <section data-testid="delivery-calendar-view" className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="overline text-[10px]">Delivery calendar</p>
          <h2 className="font-serif text-2xl text-ink mt-1">
            {groupLabel(selectedGroup)} deliveries
          </h2>
          <p className="text-xs text-muted2 mt-1">
            Pending and ready-for-dispatch invoices with an assigned delivery date.
          </p>
        </div>
        {isAdmin && (
          <div
            className="inline-flex w-full sm:w-auto rounded-xl border border-line bg-white p-1"
            data-testid="calendar-group-switch"
          >
            {["A", "B"].map((group) => (
              <button
                key={group}
                type="button"
                data-testid={`calendar-group-toggle-${group === "A" ? "sider" : "bridal"}`}
                onClick={() => setSelectedGroup(group)}
                className={
                  "min-h-10 flex-1 sm:flex-none px-4 text-xs uppercase tracking-[0.16em] "
                  + `rounded-lg transition-colors ${
                  selectedGroup === group
                    ? "bg-maroon text-ivory"
                    : "text-muted2 hover:text-maroon"
                  }`
                }
              >
                {groupLabel(group)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="calendar-summary">
        {[
          { label: "Due today", value: data.summary?.today_count ?? "—", icon: Clock },
          { label: "Due tomorrow", value: data.summary?.tomorrow_count ?? "—", icon: Truck },
          { label: "This month", value: data.summary?.month_count ?? "—", icon: CalendarDays },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            data-testid={`calendar-summary-${label.toLowerCase().replaceAll(" ", "-")}`}
            className={
              "border border-line bg-white px-2.5 py-3 sm:px-4 rounded-xl flex "
              + "items-center gap-2 sm:gap-3"
            }
          >
            <span
              className={
                "hidden sm:inline-flex h-9 w-9 rounded-lg bg-maroon/[0.07] text-maroon "
                + "items-center justify-center"
              }
            >
              <Icon size={16} strokeWidth={1.7} />
            </span>
            <div>
              <p className="text-[8px] sm:text-[10px] uppercase tracking-[0.12em] text-muted2">
                {label}
              </p>
              <p className="font-serif text-lg sm:text-xl text-ink mt-0.5">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-line bg-white rounded-2xl overflow-hidden">
        <div
          className={
            "flex items-center justify-between gap-2 px-3 py-3 sm:px-5 border-b "
            + "border-line bg-ivory/60"
          }
        >
          <button
            type="button"
            data-testid="calendar-prev-month"
            onClick={() => changeMonth(-1)}
            className={
              "h-10 w-10 rounded-lg border border-line bg-white text-muted2 "
              + "hover:text-maroon hover:border-maroon/40 inline-flex items-center "
              + "justify-center transition-colors"
            }
            aria-label="Previous month"
          >
            <ChevronLeft size={18} strokeWidth={1.8} />
          </button>
          <div className="min-w-0 text-center">
            <p
              data-testid="calendar-month-heading"
              className="font-serif text-lg sm:text-xl text-ink"
            >
              {format(visibleMonth, "MMMM yyyy")}
            </p>
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted2 mt-0.5">
              {groupLabel(selectedGroup)} pending deliveries
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid="calendar-today-btn"
              onClick={goToToday}
              className={
                "min-h-10 px-3 rounded-lg text-[11px] uppercase tracking-[0.14em] "
                + "text-maroon border border-maroon/30 bg-white hover:bg-maroon/[0.05] "
                + "transition-colors"
              }
            >
              Today
            </button>
            <button
              type="button"
              data-testid="calendar-next-month"
              onClick={() => changeMonth(1)}
              className={
                "h-10 w-10 rounded-lg border border-line bg-white text-muted2 "
                + "hover:text-maroon hover:border-maroon/40 inline-flex items-center "
                + "justify-center transition-colors"
              }
              aria-label="Next month"
            >
              <ChevronRight size={18} strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {loading ? (
          <div data-testid="calendar-loading" className="py-16 text-center text-muted2">
            <RefreshCw className="mx-auto animate-spin text-maroon" size={22} />
            <p className="mt-3 text-sm">Loading scheduled deliveries…</p>
          </div>
        ) : error ? (
          <div data-testid="calendar-error" className="py-14 px-5 text-center">
            <p className="text-sm text-[#8A2A2A]">{error}</p>
            <button
              type="button"
              data-testid="calendar-retry-btn"
              onClick={() => setRequestVersion((version) => version + 1)}
              className={
                "btn-outline-maroon !mt-4 !px-4 !py-2 !text-xs inline-flex "
                + "items-center gap-1.5"
              }
            >
              <RefreshCw size={13} strokeWidth={1.7} /> Retry
            </button>
          </div>
        ) : (
          <div className="p-2 sm:p-4">
            <div
              className={
                "grid grid-cols-7 gap-px bg-line border border-line rounded-xl "
                + "overflow-hidden"
              }
            >
              {WEEKDAYS.map((weekday) => (
                <div
                  key={weekday}
                  className={
                    "bg-ivory px-1 py-2 text-center text-[9px] sm:text-[10px] "
                    + "uppercase tracking-[0.12em] text-muted2"
                  }
                >
                  {weekday}
                </div>
              ))}
              {emptyCells.map((index) => (
                <div
                  key={`empty-${index}`}
                  className="min-h-[3.8rem] sm:min-h-[5rem] bg-ivory/35"
                />
              ))}
              {days.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const count = (deliveriesByDate.get(key) || []).length;
                const selected = selectedDate === key;
                const isToday = isSameDay(day, today);
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`calendar-day-${key}`}
                    onClick={() => setSelectedDate(key)}
                    className={
                      "relative min-h-[3.8rem] sm:min-h-[5rem] bg-white p-1.5 sm:p-2 "
                      + "text-left transition-colors focus:outline-none focus-visible:ring-2 "
                      + `focus-visible:ring-maroon ${
                      selected ? "bg-maroon/[0.06]" : "hover:bg-ivory/70"
                      }`
                    }
                    aria-pressed={selected}
                    aria-label={`${key}${count ? `, ${count} scheduled deliveries` : ""}`}
                  >
                    <span
                      className={
                        "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs "
                        + `${
                        selected
                          ? "bg-maroon text-ivory"
                          : isToday
                          ? "border border-maroon/40 text-maroon"
                          : "text-ink"
                        }`
                      }
                    >
                      {format(day, "d")}
                    </span>
                    {count > 0 && (
                      <>
                        <span
                          data-testid={`calendar-count-${key}`}
                          className={
                            "hidden sm:inline-flex absolute bottom-1.5 right-1.5 min-w-5 h-5 "
                            + "px-1 rounded-full bg-[#F2E9DA] text-[#7A5A22] text-[10px] "
                            + "font-semibold items-center justify-center"
                          }
                        >
                          {count}
                        </span>
                        <span
                          aria-hidden="true"
                          className={
                            "sm:hidden absolute bottom-1.5 right-1.5 h-1.5 w-1.5 "
                            + "rounded-full bg-maroon"
                          }
                        />
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div
        data-testid="calendar-selected-list"
        className="border border-line bg-white rounded-2xl overflow-hidden"
      >
        <div
          className="px-4 py-4 sm:px-5 border-b border-line bg-ivory/50 flex items-center gap-2"
        >
          <Package size={16} strokeWidth={1.7} className="text-maroon" />
          <div>
            <p className="overline text-[10px]">Scheduled for</p>
            <h3
              data-testid="calendar-selected-date"
              className="font-serif text-lg text-ink mt-0.5"
            >
              {selectedLabel}
            </h3>
          </div>
        </div>
        {loading ? null : selectedDeliveries.length === 0 ? (
          <div
            data-testid="calendar-empty-date"
            className="px-5 py-10 text-center text-muted2"
          >
            <CalendarDays
              className="mx-auto text-maroon/50"
              size={22}
              strokeWidth={1.5}
            />
            <p className="text-sm mt-3">
              No pending deliveries scheduled for this date.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-line">
            {selectedDeliveries.map((delivery) => (
              <article
                key={delivery.id}
                data-testid={`calendar-invoice-card-${delivery.invoice_number}`}
                className={
                  "px-4 py-4 sm:px-5 flex flex-col gap-3 sm:flex-row sm:items-center "
                  + "sm:justify-between"
                }
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-xl text-ink">
                      Invoice {delivery.invoice_number}
                    </p>
                    <span
                      className={`text-[10px] px-2 py-1 rounded-full ${
                        STATUS_COLORS[delivery.status] || "bg-ivory text-muted2"
                      }`}
                    >
                      {delivery.status}
                    </span>
                  </div>
                  <p className="text-sm text-ink mt-1 truncate">
                    {delivery.customer_name || "Customer details unavailable"}
                  </p>
                  <p className="text-xs text-muted2 mt-1">
                    {deliveryTypeLabel(delivery)} · Delivery date {delivery.internal_delivery_date}
                    {delivery.salesman ? ` · ${delivery.salesman}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`calendar-open-invoice-${delivery.invoice_number}`}
                  onClick={() => onOpenInvoice(delivery)}
                  className={
                    "btn-outline-maroon !px-3.5 !py-2 !text-xs inline-flex "
                    + "items-center justify-center gap-1.5 shrink-0"
                  }
                >
                  View details
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};